import { useQuery } from "@tanstack/react-query";
import { Check, Handshake, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "../../api/client";
import { BrandLockup } from "../../brand/BrandMark";
import { LegalFooter } from "../../legal/LegalFooter";
import { formatMoney } from "../../lib/format";
import { PRODUCT_NAME } from "../../product";
import "./marketing.css";

export interface PublicPlan {
  code: string;
  name: string;
  description: string;
  monthlyPriceMinor: number;
  annualPriceMinor: number;
  currency: string;
  customPricing: boolean;
  features: Record<string, boolean>;
  limits: Record<string, number>;
}

const fallbackPlans: PublicPlan[] = [
  {
    code: "starter",
    name: "Starter",
    description:
      "Core scheduling and customer workflows for an independent operator or small team.",
    monthlyPriceMinor: 4000,
    annualPriceMinor: 40000,
    currency: "USD",
    customPricing: false,
    features: { calendar: true, publicBooking: true, customerPortal: true },
    limits: { venues: 1, locations: 5, staff: 5 },
  },
  {
    code: "business",
    name: "Business",
    description: "Deeper operations, automation, and reporting for a growing venue business.",
    monthlyPriceMinor: 12000,
    annualPriceMinor: 120000,
    currency: "USD",
    customPricing: false,
    features: {
      calendar: true,
      publicBooking: true,
      customerPortal: true,
      inventory: true,
      sponsors: true,
      analytics: true,
      webhooks: true,
    },
    limits: { venues: 1, locations: 50, staff: 20 },
  },
  {
    code: "enterprise",
    name: "Enterprise",
    description: "Multi-venue controls, SSO, integrations, and higher operating capacity.",
    monthlyPriceMinor: 40000,
    annualPriceMinor: 400000,
    currency: "USD",
    customPricing: true,
    features: {
      calendar: true,
      publicBooking: true,
      customerPortal: true,
      inventory: true,
      sponsors: true,
      analytics: true,
      webhooks: true,
      customDomains: true,
      sso: true,
      api: true,
    },
    limits: { venues: 10, locations: 500, staff: 100 },
  },
];

const featureNames: Record<string, string> = {
  publicBooking: "Public booking",
  customerPortal: "Customer portal",
  inventory: "Inventory operations",
  sponsors: "Sponsorship management",
  analytics: "Operational analytics",
  webhooks: "Outbound webhooks",
  customDomains: "Custom domains",
  sso: "Workforce SSO",
  api: "Integration API",
};

function planHighlights(plan: PublicPlan): string[] {
  const capacity = [
    `${plan.limits.venues ?? 0} ${plan.limits.venues === 1 ? "venue" : "venues"}`,
    `${plan.limits.locations ?? 0} bookable locations`,
    `${plan.limits.staff ?? 0} staff accounts`,
  ];
  const features = Object.entries(plan.features)
    .filter(([key, enabled]) => enabled && key !== "calendar")
    .map(([key]) => featureNames[key])
    .filter((name): name is string => Boolean(name));
  return [...capacity, ...features];
}

export function MarketingPage() {
  const plans = useQuery({
    queryKey: ["public", "billing-plans"],
    queryFn: () => api<{ items: PublicPlan[] }>("/api/v1/billing/plans"),
    staleTime: 300_000,
  });
  const items = plans.data?.items?.length ? plans.data.items : fallbackPlans;

  return (
    <main className="marketing-page">
      <header className="marketing-nav">
        <Link className="marketing-brand" to="/" aria-label={`${PRODUCT_NAME} home`}>
          <BrandLockup />
        </Link>
        <nav aria-label="Public navigation">
          <a href="#capabilities">Capabilities</a>
          <a href="#sponsors">Sponsors</a>
          <a href="#pricing">Pricing</a>
          <a href="#security">Security</a>
          <Link className="button secondary" to="/login">
            Sign in
          </Link>
        </nav>
      </header>

      <section className="marketing-hero">
        <div className="marketing-hero-copy marketing-stack">
          <p className="eyebrow">Venue operations, without the blind spots</p>
          <h1>
            Run every venue, booking, payment, sponsor, and customer workflow from one accountable
            system.
          </h1>
          <p className="marketing-lede">
            {PRODUCT_NAME} gives venue teams a secure operational record from first inquiry through
            settlement, with clear roles, audited exceptions, and connected customer and sponsor
            experiences.
          </p>
          <div className="marketing-actions">
            <a className="button primary" href="#pilot">
              Request pilot access
            </a>
            <a className="button ghost" href="#pricing">
              Compare packages
            </a>
          </div>
          <p className="marketing-fine">
            Initial onboarding is invitation-only so every customer receives a controlled
            implementation.
          </p>
        </div>
        <aside className="marketing-proof" aria-label="Platform principles">
          <ShieldCheck size={34} aria-hidden="true" />
          <h2>Built for accountable operations</h2>
          <ul>
            <li>Fixed staff roles with explicit venue scope</li>
            <li>Audited conflict exceptions; never silent overrides</li>
            <li>Tenant isolation and protected sensitive actions</li>
            <li>Separate customer-payment and platform-billing boundaries</li>
          </ul>
        </aside>
      </section>

      <section id="capabilities" className="marketing-section">
        <div className="marketing-section-heading marketing-stack">
          <p className="eyebrow">A complete operating loop</p>
          <h2>Replace fragmented calendars and spreadsheets with one source of truth.</h2>
          <p>
            Keep commercial, operational, and customer work connected without losing clear ownership
            or an auditable record.
          </p>
        </div>
        <div className="capability-grid">
          <article>
            <h3>Bookings and availability</h3>
            <p>
              Portfolio calendars, recurrence, holds, policies, public requests, and controlled
              conflict resolution.
            </p>
          </article>
          <article>
            <h3>Finance and evidence</h3>
            <p>
              Quotes, invoices, online payments, refunds, reconciliation, and retained evidence for
              every offline payment.
            </p>
          </article>
          <article>
            <h3>Customers and operations</h3>
            <p>
              Customer portal, messaging, documents, inventory, privacy workflows, and auditable
              support access.
            </p>
          </article>
        </div>
      </section>

      <section id="sponsors" className="marketing-section sponsor-section">
        <div className="marketing-section-heading marketing-stack">
          <p className="eyebrow">Sponsorships belong in the operating record</p>
          <h2>Manage sponsor commitments alongside the venues and bookings they support.</h2>
          <p>
            Replace separate sponsor spreadsheets with accountable records that operations, finance,
            and relationship owners can follow together.
          </p>
        </div>
        <div className="sponsor-grid">
          <article>
            <Handshake size={30} aria-hidden="true" />
            <h3>Relationships and agreements</h3>
            <p>
              Maintain sponsor contacts, notes, agreement owners, renewal dates, and links to the
              relevant venue or booking.
            </p>
          </article>
          <article>
            <h3>Cash and in-kind value</h3>
            <p>
              Track cash and in-kind contributions, quantities, valuations, availability, status,
              and use restrictions.
            </p>
          </article>
          <article>
            <h3>Recognition obligations</h3>
            <p>
              Record promised recognition, due dates, completion status, and finance and analytics
              visibility without losing the agreement context.
            </p>
          </article>
        </div>
      </section>

      <section id="pricing" className="marketing-section pricing-section">
        <div className="marketing-section-heading marketing-stack">
          <p className="eyebrow">Simple, capacity-based packages</p>
          <h2>Choose the operating capacity you need now. Add capacity through a written quote.</h2>
        </div>
        {plans.error ? (
          <p className="pricing-status" role="status">
            Showing published launch pricing while live confirmation is unavailable.
          </p>
        ) : null}
        <div className="pricing-grid">
          {items.map((plan) => (
            <article
              className={`pricing-card ${plan.code === "business" ? "featured" : ""}`}
              key={plan.code}
            >
              {plan.code === "business" ? (
                <span className="pricing-badge">For growing teams</span>
              ) : null}
              <h3>{plan.name}</h3>
              <p>{plan.description}</p>
              <div className="plan-price">
                <strong>
                  {plan.customPricing ? "From " : ""}
                  {formatMoney(plan.monthlyPriceMinor, plan.currency)}
                </strong>
                <span>/ month</span>
              </div>
              <p className="annual-price">
                {formatMoney(plan.annualPriceMinor, plan.currency)} billed annually (two months
                included)
              </p>
              <ul>
                {planHighlights(plan).map((highlight) => (
                  <li key={highlight}>
                    <Check size={17} aria-hidden="true" />
                    {highlight}
                  </li>
                ))}
              </ul>
              <a
                className={`button ${plan.code === "business" ? "primary" : "secondary"}`}
                href="#pilot"
              >
                Discuss {plan.name}
              </a>
            </article>
          ))}
        </div>
        <p className="pricing-note">
          Subscription pricing includes the platform, routine hosting, and standard support. We do
          not add an undisclosed platform surcharge to provider fees. Customer payment processing
          remains separate in the customer’s connected payment account, and legally required taxes
          are shown before commitment.
        </p>
      </section>

      <section id="security" className="marketing-section security-band">
        <div className="marketing-section-heading marketing-stack">
          <p className="eyebrow">Enterprise-ready identity</p>
          <h2>SSO without a separate application boundary.</h2>
        </div>
        <p>
          Enterprise supports Microsoft Entra ID, Microsoft US Government, Okta, Okta Government,
          and Google Workspace through standards-based OIDC. Accounts must be invited before first
          sign-in; SSO never creates unapproved users.
        </p>
      </section>

      <section id="pilot" className="marketing-section pilot-section">
        <div className="pilot-content marketing-stack">
          <p className="eyebrow">Founding customer program</p>
          <h2>Launch with a controlled implementation, not an anonymous signup form.</h2>
          <p>
            Our initial customers receive guided setup, migration planning, and a written pilot
            offer. Any introductory discount is explicit, time-bounded, and does not change the
            published package capacity.
          </p>
          <Link className="button primary" to="/login">
            I already have an invitation
          </Link>
          <p className="marketing-fine">
            New pilot requests are handled directly by your implementation representative while
            onboarding remains limited.
          </p>
        </div>
      </section>

      <LegalFooter className="marketing-footer" />
    </main>
  );
}
